// src/pages/RoleExplorer.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import rolesData from '../data/rolesdata.json';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 50px;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
`;

const PageTitle = styled.h1`
  font-size: 48px;
  font-weight: 600;
  background: linear-gradient(45deg, #1d1d1f, #424245);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 16px;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 36px;
  }

  @media (max-width: 480px) {
    font-size: 32px;
  }
`;

const PageSubtitle = styled.p`
  font-size: 21px;
  color: #86868b;
  font-weight: 400;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-top: 40px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const RolesList = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border-radius: 18px;
  padding: 32px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  max-height: 600px;
  overflow-y: auto;
`;

const RoleDetail = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border-radius: 18px;
  padding: 32px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: sticky;
  top: 20px;

  @media (max-width: 968px) {
    position: static;
  }
`;

const SectionTitle = styled.h2`
  font-size: 28px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const RoleCard = styled.div`
  padding: 20px;
  background: ${props => props.selected ? 
    'linear-gradient(135deg, #0071e3, #0077ed)' : 
    'rgba(255, 255, 255, 0.6)'};
  border: 1px solid ${props => props.selected ? 'transparent' : '#e8e8ed'};
  border-radius: 14px;
  margin-bottom: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: ${props => props.selected ? 'white' : '#1d1d1f'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    background: ${props => props.selected ? 
      'linear-gradient(135deg, #0077ed, #0082f3)' : 
      'rgba(255, 255, 255, 0.8)'};
  }
`;

const RoleTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const RoleCategory = styled.span`
  font-size: 14px;
  color: ${props => props.selected ? 'rgba(255, 255, 255, 0.8)' : '#86868b'};
  font-weight: 500;
`;

const SalaryRange = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-top: 8px;
  color: ${props => props.selected ? 'rgba(255, 255, 255, 0.9)' : '#0071e3'};
`;

const DetailSection = styled.div`
  margin-bottom: 28px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f5f5f7;
`;

const DetailContent = styled.p`
  color: #515154;
  line-height: 1.6;
  font-size: 16px;
`;

const SkillsList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
`;

const SkillTag = styled.li`
  padding: 8px 16px;
  background: rgba(0, 113, 227, 0.1);
  color: #0071e3;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  list-style: none;
`;

const ResponsibilitiesList = styled.ul`
  color: #515154;
  line-height: 1.6;
  padding-left: 20px;
`;

const ResponsibilityItem = styled.li`
  margin-bottom: 8px;
  font-size: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SalaryHighlight = styled.div`
  background: linear-gradient(135deg, #30d158, #34c759);
  color: white;
  padding: 16px 20px;
  border-radius: 14px;
  text-align: center;
  margin-top: 20px;
  font-weight: 600;
  font-size: 18px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #86868b;
  font-size: 18px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 14px;
  border: 1px dashed #e8e8ed;
`;

const RoleExplorer = () => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    // Load roles data from JSON
    setRoles(rolesData.roles);
    // Auto-select first role on initial load
    if (rolesData.roles.length > 0 && !selectedRole) {
      setSelectedRole(rolesData.roles[0]);
    }
  }, []);

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
  };

  return (
    <PageContainer>
      <HeaderSection>
        <PageTitle>Role Explorer</PageTitle>
        <PageSubtitle>
          Explore different job profiles in the power utility industry. 
          Click on any role to view detailed descriptions, required skills, responsibilities, and salary information. Please note the salary is in USD based on United States data.
        </PageSubtitle>
      </HeaderSection>

      <TwoColumnLayout>
        <RolesList>
          <SectionTitle>Job Roles</SectionTitle>
          {roles.length > 0 ? (
            roles.map((role, index) => (
              <RoleCard
                key={index}
                selected={selectedRole && selectedRole.id === role.id}
                onClick={() => handleRoleSelect(role)}
              >
                <RoleTitle>{role.title}</RoleTitle>
                <RoleCategory selected={selectedRole && selectedRole.id === role.id}>
                  {role.category} • {role.experienceLevel}
                </RoleCategory>
                <SalaryRange selected={selectedRole && selectedRole.id === role.id}>
                  {role.salaryRange}
                </SalaryRange>
              </RoleCard>
            ))
          ) : (
            <EmptyState>No roles available to display.</EmptyState>
          )}
        </RolesList>

        <RoleDetail>
          {selectedRole ? (
            <>
              <SectionTitle>{selectedRole.title}</SectionTitle>
              
              <DetailSection>
                <DetailTitle>Description</DetailTitle>
                <DetailContent>{selectedRole.description}</DetailContent>
              </DetailSection>

              <DetailSection>
                <DetailTitle>Required Skills</DetailTitle>
                <DetailContent>
                  {selectedRole.skillsDescription}
                </DetailContent>
                <SkillsList>
                  {selectedRole.requiredSkills.map((skill, index) => (
                    <SkillTag key={index}>{skill}</SkillTag>
                  ))}
                </SkillsList>
              </DetailSection>

              <DetailSection>
                <DetailTitle>Typical Responsibilities</DetailTitle>
                <ResponsibilitiesList>
                  {selectedRole.responsibilities.map((responsibility, index) => (
                    <ResponsibilityItem key={index}>
                      {responsibility}
                    </ResponsibilityItem>
                  ))}
                </ResponsibilitiesList>
              </DetailSection>

              <SalaryHighlight>
                Average Salary: {selectedRole.salaryRange}
              </SalaryHighlight>

              <DetailSection>
                <DetailTitle>Experience Level</DetailTitle>
                <DetailContent>{selectedRole.experienceLevel}</DetailContent>
              </DetailSection>
            </>
          ) : (
            <EmptyState>Select a role to view details</EmptyState>
          )}
        </RoleDetail>
      </TwoColumnLayout>
    </PageContainer>
  );
};

export default RoleExplorer;